import { useEffect } from 'react';
import {
  useFieldArray,
  useWatch,
  type Control,
  type FieldErrors,
  type UseFormRegister,
  type UseFormSetValue,
} from 'react-hook-form';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

import { emptySellLine } from './adjustment-note-defaults';
import { errorMessage, FieldShell } from './adjustment-note-fields';
import { adjustmentLinesSum } from './adjustment-note-payload';
import { SellNoteLineRow } from './adjustment-note-sell-line-row';
import { settlementMethodsFor, type AdjustmentNoteSubmitValues } from './adjustment-note-schema';
import { formatMoney } from './final-settlement-parts';

type NoteType = 'Credit' | 'Debit';

function testidKind(noteType: NoteType): string {
  return noteType === 'Credit' ? 'credit' : 'debit';
}

interface CoreProps {
  readonly noteType: NoteType;
  readonly register: UseFormRegister<AdjustmentNoteSubmitValues>;
  readonly errors: FieldErrors<AdjustmentNoteSubmitValues>;
  readonly disabled: boolean;
}

// Sell-side note (Launchpad parity): a free-text reason and settlement method
// sit above the itemised lines. The Debit note adds free-text payment terms.
export function SellNoteCoreFields(props: CoreProps) {
  const isDebit = props.noteType === 'Debit';
  return (
    <div className="flex flex-col gap-4">
      <FieldShell id="note_reason" label="Reason" error={errorMessage(props.errors, 'note_reason')}>
        <Textarea
          id="note_reason"
          rows={2}
          placeholder={`Describe the reason for this ${isDebit ? 'debit' : 'credit'} note…`}
          disabled={props.disabled}
          {...props.register('note_reason')}
        />
      </FieldShell>
      {isDebit && (
        <FieldShell
          id="payment_terms"
          label="Payment Terms"
          error={errorMessage(props.errors, 'payment_terms')}
        >
          <Input
            id="payment_terms"
            type="text"
            autoComplete="off"
            disabled={props.disabled}
            {...props.register('payment_terms')}
          />
        </FieldShell>
      )}
      <FieldShell
        id="settlement_method"
        label="Settlement Method"
        error={errorMessage(props.errors, 'settlement_method')}
      >
        <Select
          id="settlement_method"
          disabled={props.disabled}
          {...props.register('settlement_method')}
        >
          <option value="">Select settlement method…</option>
          {settlementMethodsFor(props.noteType).map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </Select>
      </FieldShell>
    </div>
  );
}

interface SectionProps {
  readonly noteType: NoteType;
  readonly control: Control<AdjustmentNoteSubmitValues>;
  readonly register: UseFormRegister<AdjustmentNoteSubmitValues>;
  readonly setValue: UseFormSetValue<AdjustmentNoteSubmitValues>;
  readonly disabled: boolean;
}

export function SellNoteLinesSection(props: SectionProps) {
  const kind = testidKind(props.noteType);
  const { fields, append, remove } = useFieldArray<AdjustmentNoteSubmitValues, 'adjustment_lines'>({
    control: props.control,
    name: 'adjustment_lines',
  });
  const lines = useWatch({ control: props.control, name: 'adjustment_lines' });
  const currency = useWatch({ control: props.control, name: 'currency' });
  const total = adjustmentLinesSum(lines);
  // The note total is the sum of its lines, so keep the hidden total_amount
  // field in sync for validation + submit.
  useEffect(() => {
    props.setValue('total_amount', total, { shouldValidate: false });
  }, [props, total]);
  return (
    <div className="flex flex-col gap-3" data-testid={`${kind}-lines`}>
      <LinesHeader
        kind={kind}
        disabled={props.disabled || fields.length >= 50}
        onAdd={() => {
          append(emptySellLine());
        }}
      />
      <ul className="flex flex-col gap-3">
        {fields.map((field, index) => (
          <SellNoteLineRow
            key={field.id}
            noteType={props.noteType}
            index={index}
            control={props.control}
            register={props.register}
            setValue={props.setValue}
            disabled={props.disabled}
            removable={fields.length > 1}
            onRemove={() => {
              remove(index);
            }}
          />
        ))}
      </ul>
      <SupportingDetails register={props.register} disabled={props.disabled} />
      <TotalNoteBanner noteType={props.noteType} total={total} currency={currency} />
    </div>
  );
}

function LinesHeader({
  kind,
  disabled,
  onAdd,
}: {
  readonly kind: string;
  readonly disabled: boolean;
  readonly onAdd: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <Label>Adjustment Lines</Label>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        data-testid={`${kind}-lines-add`}
        onClick={onAdd}
      >
        <Plus className="size-3" aria-hidden />
        Add Line
      </Button>
    </div>
  );
}

function SupportingDetails(props: {
  readonly register: UseFormRegister<AdjustmentNoteSubmitValues>;
  readonly disabled: boolean;
}) {
  return (
    <FieldShell id="remarks" label="Supporting Details (Optional)" error={undefined}>
      <Textarea
        id="remarks"
        rows={2}
        placeholder="Additional supporting information…"
        disabled={props.disabled}
        {...props.register('remarks')}
      />
    </FieldShell>
  );
}

function TotalNoteBanner({
  noteType,
  total,
  currency,
}: {
  readonly noteType: NoteType;
  readonly total: number;
  readonly currency: string | undefined;
}) {
  const kind = testidKind(noteType);
  const palette =
    noteType === 'Credit'
      ? {
          box: 'border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/30',
          label: 'text-emerald-800 dark:text-emerald-200',
          value: 'text-emerald-900 dark:text-emerald-100',
        }
      : {
          box: 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/30',
          label: 'text-red-800 dark:text-red-200',
          value: 'text-red-900 dark:text-red-100',
        };
  return (
    <div
      className={`flex items-center justify-between rounded-md border p-3 ${palette.box}`}
      data-testid={`${kind}-total-banner`}
    >
      <span className={`text-sm font-medium ${palette.label}`}>Total {noteType} Amount</span>
      <span
        className={`text-lg font-semibold ${palette.value}`}
        data-testid={`${kind}-total-amount`}
      >
        {formatMoney(total, currency ?? null)}
      </span>
    </div>
  );
}
